/**
 * Short share links for WEEKLY FEATURED posters: luv.wtf/1, luv.wtf/2, …
 *
 * Each slot number maps to a regional feature flag on `dinnerGiveawayOptIns`
 * (same flags the WEEKLY FEATURED carousel reads). Slot order mirrors the
 * carousel display order in HomePage: Marina, North Beach, SOMA, Downtown, Berkeley.
 *
 * Short links are only handed out to signed-out sharers; signed-in users keep
 * unique `/poster/:id?ref=` links for referral attribution.
 */

import { collection, getDocs, limit, query, where } from 'firebase/firestore';

export const WEEKLY_FEATURED_SLOTS = [
  { slot: 1, featureField: 'sfmarinafeature', label: 'SF Marina' },
  { slot: 2, featureField: 'sfnorthbeachfeature', label: 'SF North Beach' },
  { slot: 3, featureField: 'sfsomafeature', label: 'SF SOMA' },
  { slot: 4, featureField: 'sfdowntownfeature', label: 'SF Downtown' },
  { slot: 5, featureField: 'berkeleyfeature', label: 'Berkeley' },
];

export function buildWeeklyFeaturedSlotUrl(origin, slot) {
  const o = (origin || '').replace(/\/$/, '');
  return `${o}/${slot}`;
}

/** Returns the slot number for a bare numeric path like "/3", or null if not a valid slot path. */
export function parseWeeklyFeaturedSlotFromPath(pathname) {
  const m = /^\/([1-9])$/.exec(pathname || '');
  if (!m) return null;
  const slot = Number(m[1]);
  return WEEKLY_FEATURED_SLOTS.some((s) => s.slot === slot) ? slot : null;
}

function optInCreatedAtMs(data) {
  const c = data?.createdAt;
  const d = c?.toDate?.() ?? (c instanceof Date ? c : null);
  return d && typeof d.getTime === 'function' ? d.getTime() : 0;
}

/**
 * Resolve a slot number to the posterId currently featured for that region.
 * Mirrors fetchRegionalFeaturedFeedAds in dinnerGiveawayFeed.js: newest opt-in
 * with the regional flag wins. Returns null when nothing is featured.
 */
export async function resolveWeeklyFeaturedSlotPosterId(firestore, slot) {
  const entry = WEEKLY_FEATURED_SLOTS.find((s) => s.slot === slot);
  if (!entry) return null;
  let snap;
  try {
    snap = await getDocs(query(
      collection(firestore, 'dinnerGiveawayOptIns'),
      where(entry.featureField, '==', true),
      limit(25),
    ));
  } catch (err) {
    console.error(`Weekly featured slot ${slot} (${entry.label}) query failed:`, err);
    return null;
  }
  const newest = snap.docs
    .slice()
    .sort((a, b) => optInCreatedAtMs(b.data()) - optInCreatedAtMs(a.data()))[0];
  if (!newest) return null;
  const rawPid = newest.data().posterId;
  const posterId = typeof rawPid === 'string' ? rawPid.trim() : String(rawPid ?? '').trim();
  return posterId || null;
}
